import { ChangeEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import gql from 'graphql-tag';
import { InputMaskChangeEvent } from 'primereact/inputmask';
import { Modal } from '@/components/Layout';
import { Header } from '@/components/Layout/Header/Header';
import { SearchBar } from '@/components/Common';
import { DataTable } from '@/components/Ui';
import { ColumnDefinition } from '@/components/Ui/DataTable/DataTable.type';
import { ClassDisciplineStatus } from '@/api/gql/graphql';
import type { SchoolClassQuery } from '@/api/gql/graphql';
import { graphqlFetch } from '@/utils/graphqlFetch';
import ClassStudentsDrawer from './ClassStudentsDrawer';

type Person = { firstName?: string | null; lastName?: string | null; email?: string | null };
type School = { _id: string; name: string; type?: string | null; status?: string | null };
type ClassItem = {
  _id: string;
  name: string;
  accessCode?: string | null;
  classRef?: { _id: string; name: string } | null;
  createdAt?: string | null;
  disciplines?: Array<{
    status: ClassDisciplineStatus;
    lessonsCount?: number | null;
    teacher?: Person | null;
    discipline?: { _id: string; name: string; type?: string | null } | null;
  }> | null;
  lessonsCount?: number | null;
  letter?: string | null;
  payToAccess?: boolean | null;
  promoYear?: string | null;
  school?: School | null;
  serie?: { _id: string; name: string } | null;
  studentsCount?: number | null;
  tags?: string[];
  teachersCount?: number | null;
  assetConfigs?: Array<{ _id?: string | null; assetType?: string | null }> | null;
};
type Row = Record<string, unknown> & {
  className: ReactNode; school: string; counts: ReactNode; access: ReactNode; createdAt: string;
};

const CLASS_FIELDS = gql`
  fragment ClassMonitoringFields on ClassOutput {
    _id
    name
    accessCode
    promoYear
    letter
    payToAccess
    studentsCount
    teachersCount
    lessonsCount
    createdAt
    tags
    school { _id name type status }
    serie { _id name }
    classRef { _id name }
    assetConfigs { _id assetType }
  }
`;

const GET_CLASSES = gql`
  ${CLASS_FIELDS}
  query GetSchoolClassesMonitoring($query: SchoolClassQuery!) {
    getSchoolClasses(query: $query) { ...ClassMonitoringFields }
  }
`;

const GET_CLASS = gql`
  ${CLASS_FIELDS}
  query GetClassMonitoring($id: MOID!) {
    getClass(id: $id) {
      ...ClassMonitoringFields
      disciplines {
        status
        lessonsCount
        teacher { firstName lastName email }
        discipline { _id name type }
      }
    }
  }
`;

const columns: ColumnDefinition[] = [
  { field: 'className', header: 'Classe', sortable: false },
  { field: 'school', header: 'Ecole' },
  { field: 'counts', header: 'Monitoring', sortable: false },
  { field: 'access', header: 'Acces', sortable: false },
  { field: 'createdAt', header: 'Creation' },
];

const formatDate = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
    : 'Non renseignee';

const formatPerson = (person?: Person | null) => {
  const fullName = [person?.firstName, person?.lastName].filter(Boolean).join(' ');
  return fullName || person?.email || 'Non assigne';
};

const Badge = ({ tone, children }: { tone: 'green' | 'red' | 'yellow' | 'gray'; children: ReactNode }) => {
  const color = {
    green: 'border-[#244A3C] bg-[#14251F] text-[#7AD7B0]',
    red: 'border-[#513030] bg-[#281818] text-[#FF9A9A]',
    yellow: 'border-[#4B3F20] bg-[#211D12] text-[#F5C84C]',
    gray: 'border-[#343434] bg-[#202020] text-[#D0D5DD]',
  }[tone];
  return <span className={`rounded-[6px] border px-2.5 py-1 text-xs font-semibold ${color}`}>{children}</span>;
};

const isEnaClass = (item?: Pick<ClassItem, 'name' | 'tags'> | null) => {
  const values = [item?.name, ...(item?.tags ?? [])];
  return values.some((value) => value?.toLowerCase().includes('ena'));
};

const getClassAccessLabel = (item?: Pick<ClassItem, 'name' | 'payToAccess' | 'tags'> | null) => {
  if (!item?.payToAccess) return 'Gratuite';
  return isEnaClass(item) ? 'Acces 365 jours' : 'Payante';
};

const normalizeClassName = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const Metric = ({
  icon,
  label,
  onClick,
  value,
}: {
  icon: string;
  label: string;
  onClick?: () => void;
  value: ReactNode;
}) => {
  const content = (
    <>
      <div className="flex size-11 shrink-0 items-center justify-center rounded-[8px] border border-[#403719] bg-[#211D12] text-primary">
        <i className={`pi ${icon}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase text-[#A5A5A5]">{label}</p>
        <p className="mt-1 text-2xl font-semibold text-white">{value}</p>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        className="flex min-h-[92px] items-center gap-4 rounded-[8px] border border-[#2C2C2C] bg-[#181818] px-5 py-4 text-left transition hover:border-[#4B3F20] hover:bg-[#1E1B12]"
        onClick={onClick}
        type="button"
      >
        {content}
      </button>
    );
  }

  return <div className="flex min-h-[92px] items-center gap-4 rounded-[8px] border border-[#2C2C2C] bg-[#181818] px-5 py-4">{content}</div>;
};

const InfoLine = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-4 border-b border-[#292929] py-3.5 last:border-b-0">
    <span className="text-xs uppercase text-[#9A9A9A]">{label}</span>
    <span className="min-w-0 text-sm text-white">{value || 'Non renseigne'}</span>
  </div>
);

const Classes = () => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [isStudentsDrawerOpen, setIsStudentsDrawerOpen] = useState(false);

  const listQuery = useQuery<ClassItem[], Error>({
    queryKey: ['classesMonitoring'],
    queryFn: async () => {
      const data = await graphqlFetch<{ getSchoolClasses: ClassItem[] }, { query: SchoolClassQuery }>(GET_CLASSES, { query: {} });
      return data.getSchoolClasses;
    },
  });
  const allClasses = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const normalizedSearch = useMemo(() => normalizeClassName(search), [search]);
  const classes = useMemo(
    () => normalizedSearch
      ? allClasses.filter((item) => normalizeClassName(item.name).includes(normalizedSearch))
      : allClasses,
    [allClasses, normalizedSearch],
  );

  const detailQuery = useQuery<ClassItem, Error>({
    queryKey: ['classMonitoring', selectedId],
    queryFn: async () => {
      const data = await graphqlFetch<{ getClass: ClassItem }, { id: string }>(GET_CLASS, { id: selectedId as string });
      return data.getClass;
    },
    enabled: isClassModalOpen && !!selectedId,
  });

  const openClassDetail = useCallback((classId: string) => {
    setSelectedId(classId);
    setIsClassModalOpen(true);
  }, []);

  const closeClassDetail = useCallback(() => {
    setIsClassModalOpen(false);
    setIsStudentsDrawerOpen(false);
  }, []);

  const rows = useMemo<Row[]>(
    () => classes.map((item) => ({
      className: (
        <button
          className={`text-left font-semibold hover:text-primary ${selectedId === item._id && isClassModalOpen ? 'text-primary' : 'text-white'}`}
          onClick={() => openClassDetail(item._id)}
          type="button"
        >
          {item.name}
          <span className="mt-1 block text-xs font-normal text-[#939393]">{item.classRef?.name || item.serie?.name || 'Sans niveau'}</span>
        </button>
      ),
      school: item.school?.name || 'Non rattachee',
      counts: (
        <div className="flex flex-wrap gap-2">
          <Badge tone="gray">{item.studentsCount ?? 0} eleves</Badge>
          <Badge tone="gray">{item.teachersCount ?? 0} profs</Badge>
          <Badge tone="gray">{item.lessonsCount ?? 0} lecons</Badge>
        </div>
      ),
      access: <Badge tone={item.payToAccess ? 'yellow' : 'gray'}>{getClassAccessLabel(item)}</Badge>,
      createdAt: formatDate(item.createdAt),
    })),
    [classes, isClassModalOpen, openClassDetail, selectedId],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement> | InputMaskChangeEvent) =>
    setSearch(event.target.value || '');
  const detail = detailQuery.data;
  const info = detail ? [
    { label: 'ID', value: detail._id },
    { label: 'Ecole', value: detail.school?.name },
    { label: 'Niveau', value: detail.classRef?.name || detail.serie?.name },
    { label: 'Annee', value: detail.promoYear },
    { label: 'Lettre', value: detail.letter },
    { label: 'Code acces', value: detail.accessCode },
    { label: 'Acces', value: getClassAccessLabel(detail) },
    { label: 'Tags', value: detail.tags?.join(', ') },
    { label: 'Configurations', value: `${detail.assetConfigs?.length ?? 0} configuration(s)` },
    { label: 'Creee le', value: formatDate(detail.createdAt) },
  ] : [];

  return (
    <div className="min-h-screen pb-8 text-white">
      <Header role="Admin" userName="Mas Aly" title="Monitoring des classes" />
      <div className="mt-2">
        <section className="min-w-0">
          <div className="mb-4 flex flex-col gap-4 rounded-[8px] border border-[#2C2C2C] bg-[#171717] px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-base font-semibold text-white">Toutes les classes</p>
              <p className="mt-1 text-sm text-[#9A9A9A]">
                {normalizedSearch
                  ? `${rows.length} resultat(s) sur ${allClasses.length} classe(s)`
                  : `${allClasses.length} classe(s) dans le monitoring`}
              </p>
            </div>
            <SearchBar placeholder="Rechercher par nom de classe" value={search} handleChange={handleSearch} className="!w-full lg:!w-[420px]" />
            {listQuery.isFetching && !listQuery.isLoading ? <span className="text-sm text-primary">Synchronisation...</span> : null}
          </div>
          {listQuery.error ? (
            <div className="rounded-[8px] bg-[#3A1717] p-4 text-sm text-[#FFB4B4]">{listQuery.error.message}</div>
          ) : (
            <div className="overflow-x-auto rounded-[8px] border border-[#2C2C2C] bg-[#151515] p-2">
              <DataTable key={normalizedSearch || 'all-classes'} columns={columns} value={rows} loading={listQuery.isLoading} withPaginator countTotal={rows.length} />
            </div>
          )}
        </section>
      </div>
      <Modal
        isOpen={isClassModalOpen}
        onClose={closeClassDetail}
        headerStyle="bg-[#161616] h-[48px]"
        modalStyle="bg-[#161616] p-0"
      >
        <div className="max-h-[calc(100vh_-_96px)] w-[920px] max-w-[calc(100vw_-_32px)] overflow-y-auto p-6 text-white">
          {detailQuery.error ? <div className="rounded-[8px] bg-[#3A1717] p-4 text-sm text-[#FFB4B4]">{detailQuery.error.message}</div> : null}
          {detailQuery.isLoading ? <div className="flex min-h-[420px] items-center justify-center text-primary"><i className="pi pi-spin pi-spinner text-3xl" /></div> : null}
          {detail && !detailQuery.isLoading ? (
            <div className="flex flex-col gap-6">
              <div>
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase text-[#9A9A9A]">Classe selectionnee</p>
                    <h2 className="mt-1 text-2xl font-semibold text-white">{detail.name}</h2>
                    <p className="mt-1 text-sm text-[#9A9A9A]">{detail.school?.name || 'Sans ecole'}</p>
                  </div>
                  <Badge tone={detail.payToAccess ? 'yellow' : 'gray'}>{getClassAccessLabel(detail)}</Badge>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Metric
                    icon="pi-users"
                    label="Eleves"
                    onClick={() => setIsStudentsDrawerOpen(true)}
                    value={detail.studentsCount ?? 0}
                  />
                  <Metric icon="pi-user" label="Profs" value={detail.teachersCount ?? 0} />
                  <Metric icon="pi-book" label="Lecons" value={detail.lessonsCount ?? 0} />
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase text-[#9A9A9A]">Informations</h3>
                  <div className="border-y border-[#292929] py-2">{info.map((item) => <InfoLine key={item.label} {...item} />)}</div>
                </div>

                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase text-[#9A9A9A]">Disciplines</h3>
                  <div className="max-h-[360px] space-y-3 overflow-y-auto pr-1">
                    {detail.disciplines?.length ? detail.disciplines.map((item) => (
                      <div className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4" key={`${item.discipline?._id}-${item.teacher?.email || 'empty'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold">{item.discipline?.name || 'Discipline inconnue'}</p>
                            <p className="mt-1 text-xs text-[#9A9A9A]">{formatPerson(item.teacher)}</p>
                          </div>
                          <Badge tone={item.status === ClassDisciplineStatus.Assigned ? 'green' : 'red'}>
                            {item.status === ClassDisciplineStatus.Assigned ? 'Assignee' : 'Non assignee'}
                          </Badge>
                        </div>
                        <p className="mt-3 text-xs text-[#9A9A9A]">{item.lessonsCount ?? 0} lecons rattachees</p>
                      </div>
                    )) : <p className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4 text-sm text-[#9A9A9A]">Aucune discipline rattachee</p>}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
      <ClassStudentsDrawer
        classId={detail?._id ?? selectedId}
        className={detail?.name}
        isOpen={isStudentsDrawerOpen}
        onClose={() => setIsStudentsDrawerOpen(false)}
        studentsCount={detail?.studentsCount}
      />
    </div>
  );
};

export default Classes;
